/**
 * The goal-category code a plan WRITES, never a label.
 *
 * ☠️ Found 2026-09-28 in production: 41 goals whose category code read
 * "(unknown Goal Category: (unknown Goal Category: … physical-needs))))))",
 * wrapped 1–6 deep, seven goals at each depth: one plan republished six times.
 * The ratchet: when a stored code no longer resolves, the loader DISPLAYS
 * "(unknown Goal Category: <code>)"; the builder then wrote
 * `categoryCode || goalCategory`, i.e. that display, AS THE CODE; the next
 * load failed to resolve it and wrapped it again. The subcategory learnt this
 * lesson under ADR-0022; the category never did.
 *
 * Rules, all pure:
 *   - a placeholder label is never a code;
 *   - an unresolvable stored code is KEPT (it is the only clue to what the
 *     goal was filed under), after unwrapping any earlier damage, so an old
 *     wrapped goal heals to its original code on the next publish instead of
 *     growing another layer.
 */

const UNKNOWN_PREFIX = "(unknown Goal Category";

/** Is this one of the loader's "(unknown Goal Category…)" labels? */
export function isUnknownCategoryLabel(s: string | undefined | null): boolean {
  return typeof s === "string" && s.trimStart().startsWith(UNKNOWN_PREFIX);
}

/**
 * Strip every "(unknown Goal Category: …)" wrapper, returning the original
 * code, or "" when nothing is left (the bare "(unknown Goal Category)").
 */
export function unwrapUnknownCategory(s: string | undefined | null): string {
  let out = (s ?? "").trim();
  while (out.startsWith(UNKNOWN_PREFIX)) {
    const inner = out.slice(UNKNOWN_PREFIX.length);
    if (!inner.startsWith(":")) return "";               // "(unknown Goal Category)"
    out = inner.slice(1).trim().replace(/\)$/, "").trim();
  }
  return out;
}

export interface CategoryWriteInput {
  /** The resolved hierarchy code (OMON-028), when there is one. */
  categoryCode?: string;
  /** The display string shown in the builder; may be a placeholder label. */
  goalCategory?: string;
  /** The code as stored on the goal doc, resolvable or not. */
  storedCategoryCode?: string;
}

/**
 * The `{code, display}` to write for a goal's category. `displayFor` resolves
 * a code against the live catalogue (undefined when it doesn't resolve).
 */
export function categoryForWrite(
  g: CategoryWriteInput,
  displayFor: (code: string) => string | undefined,
): { code: string; display: string } {
  const label = isUnknownCategoryLabel(g.goalCategory) ? "" : (g.goalCategory || "");
  const code = g.categoryCode
    || unwrapUnknownCategory(g.storedCategoryCode)
    || label;
  // Unresolved: write the code itself as the display rather than a label, so a
  // reader that trusts the embedded display (the carer app) shows the honest
  // stored value, and nothing is ever wrapped.
  const display = (code && displayFor(code)) || label || code;
  return { code, display };
}
